import {
  useMutation,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query'
import { rolesService } from '../services/roles/roles.service'
import type {
  CreateRoleDto,
  RoleDetail,
  UpdateRoleArgs,
} from '../services/roles/roles.types'
import { roleKeys } from '../queries/roleKeys'

/**
 * `POST /roles` — create a custom role.
 * Invalidates every role list / detail on success, then runs the caller's
 * `onSuccess` (if any).
 */
export const useCreateRoleMutation = (
  options?: UseMutationOptions<RoleDetail, Error, CreateRoleDto>
) => {
  const queryClient = useQueryClient()
  return useMutation<RoleDetail, Error, CreateRoleDto>({
    mutationFn: rolesService.createRole,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.all })
      options?.onSuccess?.(...args)
    },
  })
}

/** `PATCH /roles/{roleId}` — update a role. */
export const useUpdateRoleMutation = (
  options?: UseMutationOptions<RoleDetail, Error, UpdateRoleArgs>
) => {
  const queryClient = useQueryClient()
  return useMutation<RoleDetail, Error, UpdateRoleArgs>({
    mutationFn: rolesService.updateRole,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.all })
      options?.onSuccess?.(...args)
    },
  })
}

/** `DELETE /roles/{roleId}` — soft-delete a role. */
export const useDeleteRoleMutation = (
  options?: UseMutationOptions<unknown, Error, number>
) => {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, number>({
    mutationFn: rolesService.deleteRole,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.all })
      options?.onSuccess?.(...args)
    },
  })
}

/** `POST /roles/{roleId}/duplicate` — clone a role. */
export const useDuplicateRoleMutation = (
  options?: UseMutationOptions<RoleDetail, Error, number>
) => {
  const queryClient = useQueryClient()
  return useMutation<RoleDetail, Error, number>({
    mutationFn: rolesService.duplicateRole,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.all })
      options?.onSuccess?.(...args)
    },
  })
}

/** `POST /roles/{roleId}/restore` — restore a deleted role. */
export const useRestoreRoleMutation = (
  options?: UseMutationOptions<unknown, Error, number>
) => {
  const queryClient = useQueryClient()
  return useMutation<unknown, Error, number>({
    mutationFn: rolesService.restoreRole,
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.all })
      options?.onSuccess?.(...args)
    },
  })
}
